import { useAuth } from '../../contexts/AuthContext';
import { useApp } from '../../contexts/AppContext';
import { useState, useEffect, useCallback } from 'react';
import * as PlatformApi from '../../services/platformApi';
import { IafItemSvc, IafFetch, IafScripts } from '../../services/platformApi';
import { DisplayPointEditor } from './DisplayPointEditor';
import {
  type CatalogCollection,
  type CatalogEntry,
  getCatalogCollection,
  createCatalogCollection,
  listCatalogEntries,
  addCatalogEntry,
  updateCatalogEntry,
  deleteCatalogEntry,
  seedCatalogFromJson,
  generateTypeCode,
} from './equipmentCatalog';
import {
  TELEMETRY_COLL_USER_TYPE,
  DISPLAY_POINTS_USER_TYPE,
  ASSET_TYPES,
  MODEL_ELEMENTS,
  DISPLAY_POINT_DESCRIPTIONS,
  DEFAULT_DISPLAY_POINTS,
  getCollectionSettings,
  getSourceIdFromElement,
  getEmptyDisplayPointForm,
  type AssetTypeValue,
} from './constants';
import type {
  TelemetryCollection,
  TelemetryItem,
  DisplayPoint,
  DisplayPointItem,
  DisplayPointsCollection,
  ModelElement,
  AggregationType,
} from './types';
import './TelemetryManagementPage.css';
import {values} from "lodash-es";

// ============================================================================
// SPARKLINE COMPONENT
// ============================================================================

interface SparklineProps {
  values: number[];
  displayPoint: DisplayPointItem | null;
  width?: number;
  height?: number;
}

function SparklineSVG({ values, displayPoint, width = 160, height = 40 }: SparklineProps) {
  if (values.length < 2) {
    return (
      <svg width={width} height={height} className="sparkline-svg">
        <text x={width/2} y={height/2} textAnchor="middle" fill="#666" fontSize="10">
          Not enough data
        </text>
      </svg>
    );
  }

  const padding = 2;
  const chartWidth = width - padding * 2;
  const chartHeight = height - padding * 2;

  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;

  // Normalize values to chart coordinates
  const points = values.map((v, i) => {
    const x = padding + (i / (values.length - 1)) * chartWidth;
    const y = padding + chartHeight - ((v - min) / range) * chartHeight;
    return { x, y, value: v };
  });

  // Create path
  const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ');

  // Create area fill path
  const areaD = pathD + ` L ${points[points.length - 1].x.toFixed(1)} ${height - padding} L ${padding} ${height - padding} Z`;

  // Determine color based on display point ranges
  let strokeColor = '#10b981'; // default green
  let fillColor = 'rgba(16, 185, 129, 0.2)';

  if (displayPoint?.ranges && displayPoint.ranges.length > 0) {
    const latestValue = values[values.length - 1];
    // Find which range the latest value falls into
    for (const range of displayPoint.ranges) {
      if (latestValue >= range.from && latestValue <= range.to) {
        // feeling: 0=good (green), 1=ok (yellow), 2=bad (red)
        if (range.feeling === 0) {
          strokeColor = '#10b981';
          fillColor = 'rgba(16, 185, 129, 0.2)';
        } else if (range.feeling === 1) {
          strokeColor = '#f59e0b';
          fillColor = 'rgba(245, 158, 11, 0.2)';
        } else {
          strokeColor = '#ef4444';
          fillColor = 'rgba(239, 68, 68, 0.2)';
        }
        break;
      }
    }
  }

  // Draw range bands if display point has ranges
  const rangeBands: React.ReactElement[] = [];
  if (displayPoint?.ranges) {
    displayPoint.ranges.forEach((r, i) => {
      const fromY = padding + chartHeight - ((Math.max(r.from, min) - min) / range) * chartHeight;
      const toY = padding + chartHeight - ((Math.min(r.to, max) - min) / range) * chartHeight;
      const bandHeight = Math.abs(fromY - toY);

      let bandColor = 'rgba(16, 185, 129, 0.1)';
      if (r.feeling === 1) bandColor = 'rgba(245, 158, 11, 0.1)';
      else if (r.feeling === 2) bandColor = 'rgba(239, 68, 68, 0.1)';

      if (bandHeight > 0) {
        rangeBands.push(
          <rect
            key={`band-${i}`}
            x={padding}
            y={Math.min(fromY, toY)}
            width={chartWidth}
            height={bandHeight}
            fill={bandColor}
          />
        );
      }
    });
  }

  return (
    <svg width={width} height={height} className="sparkline-svg">
      {/* Range bands */}
      {rangeBands}

      {/* Area fill */}
      <path d={areaD} fill={fillColor} />

      {/* Line */}
      <path
        d={pathD}
        fill="none"
        stroke={strokeColor}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {/* Latest point dot */}
      <circle
        cx={points[points.length - 1].x}
        cy={points[points.length - 1].y}
        r="3"
        fill={strokeColor}
      />
    </svg>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function TelemetryManagementPage() {
  const { createCtx, accessToken } = useAuth();
  const { selectedProject, applicationNamespaces } = useApp();

  // State
  const [collections, setCollections] = useState<TelemetryCollection[]>([]);
  // Collections paging/filter state
  const [collPage, setCollPage] = useState(0);
  const [collPageSize, setCollPageSize] = useState(50);
  const [collTotal, setCollTotal] = useState(0);
  // _userType filtering is client-side only; we precompute all available types
  const [availableUserTypes, setAvailableUserTypes] = useState<string[]>([]);
  const [selectedUserType, setSelectedUserType] = useState<string>(''); // empty = All
  const [collectionsAllCache, setCollectionsAllCache] = useState<TelemetryCollection[] | null>(null);
  const [selectedCollection, setSelectedCollection] = useState<TelemetryCollection | null>(null);
  const [telemetryItems, setTelemetryItems] = useState<TelemetryItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Form state for new collection
  // Legacy state (kept for backwards compat but not used in UI now)
  const [newCollectionAssetType, setNewCollectionAssetType] = useState<AssetTypeValue>(ASSET_TYPES.HYDRONIC_CHILLER);
  const [newCollectionAggregation, setNewCollectionAggregation] = useState<AggregationType>('raw');

  // Form state for new telemetry item
  const [selectedModelElement, setSelectedModelElement] = useState<string>('');
  const [newItemName, setNewItemName] = useState('');

  // Active tab
  const [activeTab, setActiveTab] = useState<'collections' | 'items' | 'displayPoints' | 'catalog'>('collections');

  // Model elements state (for future API fetch)
  const [modelElements] = useState<ModelElement[]>(MODEL_ELEMENTS);
  const [useApiForElements, setUseApiForElements] = useState(false);

  // Display points state
  const [displayPointsCollection, setDisplayPointsCollection] = useState<DisplayPointsCollection | null>(null);
  const [displayPoints, setDisplayPoints] = useState<DisplayPointItem[]>([]);
  const [isEditingDisplayPoint, setIsEditingDisplayPoint] = useState(false);
  const [editingDisplayPoint, setEditingDisplayPoint] = useState<DisplayPointItem | null>(null);
  const [displayPointForm, setDisplayPointForm] = useState<DisplayPoint>(getEmptyDisplayPointForm());

  // Equipment catalog state
  const [catalogCollection, setCatalogCollection] = useState<CatalogCollection | null>(null);
  const [catalogEntries, setCatalogEntries] = useState<CatalogEntry[]>([]);
  const [isCatalogLoading, setIsCatalogLoading] = useState(false);
  const [catalogError, setCatalogError] = useState<string | null>(null);
  // Catalog form/edit state
  const [catFormCategory, setCatFormCategory] = useState<string>('');
  const [catFormTypeName, setCatFormTypeName] = useState<string>('');
  const [catFormTypeCode, setCatFormTypeCode] = useState<string>('');
  const [catFormOverrideCode, setCatFormOverrideCode] = useState<boolean>(false);
  const [editingCatalogEntryId, setEditingCatalogEntryId] = useState<string | null>(null);

  // New collection pickers (catalog-driven)
  const [pickerCategory, setPickerCategory] = useState<string>('');
  const [pickerTypeName, setPickerTypeName] = useState<string>('');
  const [pickerTypeCode, setPickerTypeCode] = useState<string>('');
  const [pickerOverrideCode, setPickerOverrideCode] = useState<boolean>(false);

  // Collection creation modal state
  const [isCreateCollectionModalOpen, setIsCreateCollectionModalOpen] = useState(false);
  const [newCollectionGranularity, setNewCollectionGranularity] = useState<'seconds' | 'minutes' | 'hours'>('seconds');
  const [newCollectionExpiry, setNewCollectionExpiry] = useState<number>(60 * 60 * 24); // 24 hours in seconds

  // Sensor readings visualization state
  interface MetricViz {
    pointName: string;
    displayPoint: DisplayPointItem | null;
    values: number[];
    timestamps: string[];
    min: number;
    max: number;
    avg: number;
    latest: number;
  }

  interface DeviceViz {
    sourceId: string;
    collectionName: string;
    metrics: MetricViz[];
  }
  const [deviceReadingsViz, setDeviceReadingsViz] = useState<DeviceViz[]>([]);

    // Get project namespace
    const getProjectNamespace = useCallback((): string[] => {
        const namespaces = selectedProject?._namespaces || [];
        return namespaces.length > 0 ? [namespaces[0]] : [];
    }, [selectedProject]);

    // Create context for API calls
    const getCtx = useCallback(() => {
        const namespaces = getProjectNamespace();
        return {
            authToken: accessToken || '',
            _namespaces: namespaces,
            project: selectedProject,
        };
    }, [accessToken, getProjectNamespace, selectedProject]);

  // ==========================================================================
  // EQUIPMENT CATALOG LOAD/SEED
  // ==========================================================================
  const refreshCatalog = useCallback(async () => {
    if (!accessToken || !selectedProject) return;
    setIsCatalogLoading(true);
    setCatalogError(null);
    try {
      const ctx = getCtx();
      let coll = await getCatalogCollection(ctx);
      setCatalogCollection(coll);
      if (coll) {
        const entries = await listCatalogEntries(coll, ctx);
        setCatalogEntries(entries);
      } else {
        setCatalogEntries([]);
      }
    } catch (e: any) {
      console.error('Error loading catalog', e);
      setCatalogError(e?.message || 'Failed to load catalog');
    } finally {
      setIsCatalogLoading(false);
    }
  }, [accessToken, selectedProject, getCtx]);

  useEffect(() => {
    refreshCatalog();
  }, [refreshCatalog]);

  // Initialize pickers and catalog form defaults when entries load
  useEffect(() => {
    if (catalogEntries.length > 0 && !pickerCategory) {
      const firstCat = Array.from(new Set(catalogEntries.map(e => e.category)))[0];
      setPickerCategory(firstCat || '');
      const firstType = catalogEntries.find(e => e.category === firstCat);
      setPickerTypeName(firstType?.typeName || '');
      setPickerTypeCode(firstType?.typeCode || '');
    }
  }, [catalogEntries]);

  // ============================================================================
  // COLLECTIONS DATA FETCHING
  // ============================================================================

  // Helper: fetch ALL pages of named user items using server paging (no includeAll support)
  const fetchAllNamedUserItems = useCallback(async (
    query: Record<string, unknown>,
    ctx: ReturnType<typeof getCtx>,
    pageSize = 250,
    maxPages = 500
  ): Promise<TelemetryCollection[]> => {
    const all: TelemetryCollection[] = [];
    let offset = 0;
    let page = 0;
    // Loop until we collect all pages or hit guard
    // NOTE: We keep _userType in the server-side query to minimize total rows fetched
    // See PLATFORM_API_DOCUMENTATION: getNamedUserItems does NOT support includeAll
    // so we aggregate pages manually.
    // eslint-disable-next-line no-constant-condition
    while (true) {
      if (page >= maxPages) {
        console.warn('[TelemetryManagement] Reached maxPages guard while fetching collections. Consider refining _userType filter.');
        break;
      }
      const res = await IafItemSvc.getNamedUserItems(
        { query },
        ctx,
        { _pageSize: pageSize, _offset: offset }
      );
      const list = (res._list || []) as TelemetryCollection[];
      all.push(...list);
      const total = typeof res._total === 'number' ? res._total : undefined;
      if (total != null && all.length >= total) {
        break; // fetched all
      }
      if (list.length < pageSize) {
        break; // no more items
      }
      offset += pageSize;
      page += 1;
    }
    return all;
  }, [getCtx]);

  const fetchCollections = useCallback(async () => {
    if (!accessToken || !selectedProject) return;

    setIsLoading(true);
    setError(null);

    try {
      const ctx = getCtx();
      const q: Record<string, unknown> = {
        _itemClass: 'NamedTelemetryCollection',
        _kind: 'collection',
      };
      // Always fetch all using paging loop; then slice on client
      const allList = await fetchAllNamedUserItems(q, ctx, 250);
      setCollectionsAllCache(allList);

      // Build available _userType dropdown options (distinct, sorted)
      const typesSet = new Set<string>();
      for (const item of allList) {
        if (item?._userType) typesSet.add(item._userType);
      }
      const types = Array.from(typesSet).sort((a, b) => a.localeCompare(b));
      setAvailableUserTypes(types);

      // Apply client-side filter
      const filtered = selectedUserType
        ? allList.filter((c) => (c._userType || '') === selectedUserType)
        : allList;
      const start = collPage * collPageSize;
      const end = start + collPageSize;
      setCollections(filtered.slice(start, end));
      setCollTotal(filtered.length || 0);
    } catch (err) {
      console.error('Error fetching collections:', err);
      setError(`Failed to fetch collections: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  }, [accessToken, selectedProject, getCtx, fetchAllNamedUserItems, selectedUserType, collPage, collPageSize]);

  const fetchTelemetryItems = useCallback(async (collection: TelemetryCollection) => {
    if (!accessToken || !collection._userItemId) return;

    setIsLoading(true);
    setError(null);

    try {
      const ctx = getCtx();
      const result = await IafItemSvc.getRelatedItems(
        collection._userItemId,
        {},
        ctx,
        { page: { _pageSize: 1000 } }
      );
      setTelemetryItems(result._list || []);
    } catch (err) {
      console.error('Error fetching telemetry items:', err);
      setError(`Failed to fetch telemetry items: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  }, [accessToken, getCtx]);

  // Load collections on mount
  useEffect(() => {
    fetchCollections();
  }, [fetchCollections]);

  // When switching filter or page size, reset to first page
  useEffect(() => {
    setCollPage(0);
    // When filter/page size changes and we already cached all, recompute visible slice immediately
    if (collectionsAllCache) {
      const all = collectionsAllCache;
      const filtered = selectedUserType
        ? all.filter((c) => (c._userType || '') === selectedUserType)
        : all;
      const start = 0;
      const end = collPageSize;
      setCollections(filtered.slice(start, end));
      setCollTotal(filtered.length);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUserType, collPageSize]);

  // Load items when collection is selected
  useEffect(() => {
    if (selectedCollection) {
      fetchTelemetryItems(selectedCollection);
    } else {
      setTelemetryItems([]);
    }
  }, [selectedCollection, fetchTelemetryItems]);

  // ============================================================================
  // COLLECTION MANAGEMENT
  // ============================================================================

  const createCollection = async () => {
    if (!accessToken || !selectedProject) {
      setError('No project selected');
      return;
    }

    // Validate catalog-based selections
    if (!pickerCategory || !pickerTypeName || !pickerTypeCode) {
      setError('Please select equipment category and type, and ensure a valid code');
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const ctx = getCtx();
      const assetTypeCode = pickerTypeCode;
      const shortName = `${newCollectionAggregation}_${assetTypeCode}`.substring(0, 22);

      // Check if collection already exists
      const existingResult = await IafItemSvc.getNamedUserItems(
        { query: { _userType: TELEMETRY_COLL_USER_TYPE, _shortName: shortName } },
        ctx,
        { _pageSize: 1 }
      );

      if (existingResult._list?.length > 0) {
        await IafItemSvc.deleteNamedUserItem(existingResult._list[0]._id, ctx, {});
      }

      const collectionData = {
        _itemClass: 'NamedTelemetryCollection',
        _userType: TELEMETRY_COLL_USER_TYPE,
        ...getCollectionSettings(pickerTypeName as any, newCollectionAggregation, newCollectionGranularity, newCollectionExpiry),
        _name: `${pickerTypeName} Collection`,
        _shortName: shortName,
        _description: `${pickerTypeName} Telemetry ${newCollectionAggregation}`,
        _version: {
          _userAttributes: {
            assetTypeCode,
            'Asset Type': pickerTypeName,
            aggregation: newCollectionAggregation,
          },
        },
        _namespaces: selectedProject._namespaces,
      };

      const baseUri = IafFetch.CONFIG.itemServiceOrigin;
      const namespace = selectedProject._namespaces?.[0];

      const response = await fetch(
        `${baseUri}/itemsvc/api/v1/nameduseritems?nsfilter=${namespace}`,
        {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: accessToken,
          },
          body: JSON.stringify([collectionData]),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      setSuccessMessage(`Collection "${collectionData._name}" created successfully!`);
      setIsCreateCollectionModalOpen(false);
      // Reset form
      setPickerCategory('');
      setPickerTypeName('');
      setPickerTypeCode('');
      setNewCollectionAggregation('raw');
      setNewCollectionGranularity('seconds');
      setNewCollectionExpiry(60 * 60 * 24);
      await fetchCollections();
    } catch (err) {
      console.error('Error creating collection:', err);
      setError(`Failed to create collection: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const openCreateCollectionModal = () => {
    setPickerCategory('');
    setPickerTypeName('');
    setPickerTypeCode('');
    setNewCollectionAggregation('raw');
    setNewCollectionGranularity('seconds');
    setNewCollectionExpiry(60 * 60 * 24);
    setIsCreateCollectionModalOpen(true);
  };

  const handleAggregationChange = (agg: AggregationType) => {
    setNewCollectionAggregation(agg);
    // Set appropriate defaults
    if (agg === 'raw') {
      setNewCollectionGranularity('seconds');
      setNewCollectionExpiry(60 * 60 * 24); // 24 hours
    } else if (agg === 'minutes') {
      setNewCollectionGranularity('minutes');
      setNewCollectionExpiry(60 * 60 * 24 * 7); // 7 days
    } else if (agg === 'hourly') {
      setNewCollectionGranularity('hours');
      setNewCollectionExpiry(60 * 60 * 24 * 30); // 30 days
    }
  };

  const deleteCollection = async (collection: TelemetryCollection) => {
    if (!accessToken || !confirm(`Delete collection "${collection._name}"?`)) return;

    setIsLoading(true);
    setError(null);

    try {
      const ctx = getCtx();
      await IafItemSvc.deleteNamedUserItem(collection._id, ctx, {});
      setSuccessMessage(`Collection "${collection._name}" deleted!`);

      if (selectedCollection?._id === collection._id) {
        setSelectedCollection(null);
      }

      await fetchCollections();
    } catch (err) {
      console.error('Error deleting collection:', err);
      setError(`Failed to delete collection: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  // ============================================================================
  // TELEMETRY ITEM MANAGEMENT
  // ============================================================================

  const createTelemetryItem = async () => {
    if (!accessToken || !selectedCollection || !selectedModelElement) {
      setError('Please select a collection and model element');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const element = modelElements.find((e) => e.source_id === selectedModelElement);

      if (!element) {
        throw new Error('Model element not found');
      }

      if (!selectedCollection._userItemId) {
        throw new Error('Collection userItemId is missing');
      }

      const collectionNamespaces = selectedCollection._namespaces || [];
      const projectNamespaces = selectedProject?._namespaces || [];
      const allNamespaces = [
        ...new Set([...collectionNamespaces])
      ];

      console.log('Creating telemetry item with namespaces:', allNamespaces);
      const ctx = getCtx();

      const tipVersion = selectedCollection._versions?.find((v) => v._isTip);
      const assetType = tipVersion?._userAttributes?.['Asset Type'] || '';

      const itemData = {
        _sourceId: getSourceIdFromElement(element),
        name: newItemName || element.properties['BA Name']?.val || 'Unnamed Device',
        deviceType: assetType,
        type: assetType,
      };

      await IafItemSvc.createRelatedItemsBulk(
        selectedCollection._userItemId,
        [itemData],
        ctx
      );

      setSuccessMessage(`Telemetry item "${itemData.name}" created!`);
      setSelectedModelElement('');
      setNewItemName('');
      await fetchTelemetryItems(selectedCollection);
    } catch (err) {
      console.error('Error creating telemetry item:', err);
      setError(`Failed to create telemetry item: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const deleteTelemetryItem = async (item: TelemetryItem) => {
    if (!accessToken || !selectedCollection || !selectedCollection._userItemId || !confirm(`Delete item "${item.name}"?`)) return;

    setIsLoading(true);
    setError(null);

    try {
      const collectionNamespaces = selectedCollection._namespaces || [];
      const projectNamespaces = selectedProject?._namespaces || [];
      const allNamespaces = [
        ...new Set([...collectionNamespaces])
      ];
      const ctx = getCtx();

      await IafItemSvc.deleteRelatedItems(
        selectedCollection._userItemId,
        [item._id],
        ctx
      );
      setSuccessMessage(`Item "${item.name}" deleted!`);
      await fetchTelemetryItems(selectedCollection);
    } catch (err) {
      console.error('Error deleting telemetry item:', err);
      setError(`Failed to delete telemetry item: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  // ============================================================================
  // DEBUG: FETCH SENSOR READINGS
  // ============================================================================

  // Multi-select of telemetry item IDs (prepopulated when a collection is selected)
  const [selectedTelItemIds, setSelectedTelItemIds] = useState<string[]>([]);
  // UI: show/hide filters panel for a cleaner layout
  const [showReadingFilters, setShowReadingFilters] = useState<boolean>(false);

  // Optional processing of readings via IafScripts (user type: reading_process)
  const [applyReadingProcessor, setApplyReadingProcessor] = useState<boolean>(true);
  const [readingProcessorInfo, setReadingProcessorInfo] = useState<null | {
    scriptId: string;
    name?: string;
  }>(null);
  const [readingProcessorFn, setReadingProcessorFn] = useState<null | ((reading: any, ctx: any) => Promise<any> | any)>(null);

  const loadReadingProcessor = useCallback(async () => {
    if (!accessToken || !selectedProject) {
      setReadingProcessorFn(null);
      setReadingProcessorInfo(null);
      return;
    }

    try {
      const ctx = getCtx();
      // Try to get a script by user type "reading_process"
      const result: any = await IafScripts.getScripts({ query: { _userType: 'reading_process' } }, ctx, { _pageSize: 1 });
      const list: any[] = Array.isArray(result) ? result : (result?._list || []);
      if (!list || list.length === 0) {
        console.log('Reading processor: no script with _userType "reading_process" found.');
        setReadingProcessorFn(null);
        setReadingProcessorInfo(null);
        return;
      }

      const script = list[0];

      // Helper to extract JS source from script or its versions
      let jsCode: string | undefined = script?._version?._userData;

      // If not embedded on the item, try fetching versions and take tip/latest
      if (!jsCode) {
        try {
          const versionsRes: any = await (IafScripts as any).getVersions(script._id, {}, ctx);
          const versions: any[] = Array.isArray(versionsRes) ? versionsRes : (versionsRes?._list || []);
          let chosen: any = null;
          if (versions && versions.length > 0) {
            chosen = versions.find((v: any) => v._id === script._tipId) || versions.find((v: any) => v._isTip) || versions[0];
          }
          jsCode = chosen?._userData || jsCode;
        } catch (verErr) {
          console.warn('Reading processor: could not fetch versions:', verErr);
        }
      }

      if (!jsCode || typeof jsCode !== 'string') {
        console.warn('Reading processor: script found but no JS code in _userData.');
        setReadingProcessorFn(null);
        setReadingProcessorInfo({ scriptId: script._id, name: script._name });
        return;
      }

      // Allow scripts authored as ESM with `export` keywords by stripping them.
      const cleaned = jsCode.replace(/\bexport\s+/g, '');
      // Build a callable that returns either processReadings(reading, ctx) or run(reading, ctx)
      let builtFn: any;
      try {
        // eslint-disable-next-line no-new-func
        const factory = new Function(`${cleaned}\n;return (typeof processReadings!=="undefined"?processReadings:(typeof run!=="undefined"?run:undefined));`);
        builtFn = factory();
      } catch (e) {
        console.error('Reading processor: failed to evaluate script code:', e);
        builtFn = null;
      }

      if (typeof builtFn !== 'function') {
        console.warn('Reading processor: script does not define function processReadings or run.');
        setReadingProcessorFn(null);
        setReadingProcessorInfo({ scriptId: script._id, name: script._name });
        return;
      }

      // Wrap to support both sync and async implementations safely
      const wrapped = async (reading: any, ctxArg: any) => {
        try {
          const res = builtFn.length >= 2 ? await builtFn(reading, ctxArg) : await builtFn(reading);
          return res;
        } catch (e) {
          console.warn('Reading processor: error while processing a reading. Using original reading.', e);
          return reading;
        }
      };

      setReadingProcessorFn(() => wrapped);
      setReadingProcessorInfo({ scriptId: script._id, name: script._name });
      console.log('Reading processor loaded:', { id: script._id, name: script._name });
    } catch (err) {
      console.warn('Reading processor: error while loading script:', err);
      setReadingProcessorFn(null);
      setReadingProcessorInfo(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, selectedProject]);

  // Load/refresh processor when auth/project changes
  useEffect(() => {
    void loadReadingProcessor();
  }, [loadReadingProcessor]);

  // When telemetry items are (re)loaded for the selected collection, preselect all by default
  useEffect(() => {
    // telemetryItems is scoped to the selected collection elsewhere in the page
    // Pre-populate with all telemetry item IDs so the filter is ready immediately
    if (Array.isArray(telemetryItems) && telemetryItems.length > 0) {
      setSelectedTelItemIds(telemetryItems.map((ti: any) => ti._id).filter(Boolean));
    } else {
      setSelectedTelItemIds([]);
    }
  }, [selectedCollection, telemetryItems]);

  const fetchSensorReadings = async () => {
    if (!accessToken || collections.length === 0) {
      setError('No collections available');
      return;
    }

    setIsLoading(true);
    setError(null);
    setDeviceReadingsViz([]);

    try {
      const ctx = getCtx();
      const max = new Date();
      const min = new Date();
      min.setDate(min.getDate() - 1); // Last 24 hours

      const minIso = min.toISOString().split('.')[0] + 'Z';
      const maxIso = max.toISOString().split('.')[0] + 'Z';

      const sensorReadingsMap: Record<string, Record<string, unknown>[]> = {};

      console.log('='.repeat(60));
      console.log('FETCHING SENSOR READINGS');
      console.log('='.repeat(60));
      console.log('Time range:', minIso, 'to', maxIso);
      // If a specific collection is selected, limit fetch to it; otherwise query all
      const collectionsToQuery = selectedCollection ? [selectedCollection] : collections;
      console.log('Collections to query:', collectionsToQuery.length);

      // Use the multi-select chosen telemetry item IDs
      const telItemIdsFilter = (selectedTelItemIds || []).filter(Boolean);

      for (const coll of collectionsToQuery) {
        const collectionName = coll._shortName || coll._name || coll._id;
        console.log(`\nQuerying collection: ${collectionName} (ID: ${coll._id})`);

        // Use aggregateReadings with MongoDB pipeline to fetch readings
        const match: Record<string, unknown> = {
          _ts: {
            $gte: minIso,
            $lte: maxIso,
          },
        };

        // If telemetry item ids are provided, filter by telemetry item id.
        // Some datasets store it under _tsMetadata._telItemId, others under root _telItemId.
        // Additionally, IDs may be stored as ObjectId/number while our UI holds strings.
        // Use $expr + $toString so single selections also match regardless of underlying type.
        if (telItemIdsFilter.length > 0) {
          (match as any)["$or"] = [
            // Direct, fast-path matches when types already align
            { _telItemId: { $in: telItemIdsFilter } as any },
            { ["_tsMetadata._telItemId"]: { $in: telItemIdsFilter } as any },
            { _tsMetadata: { _telItemId: { $in: telItemIdsFilter } } as any },
            // Type-agnostic matches using $expr + $toString
            { $expr: { $in: [ { $toString: "$_telItemId" }, telItemIdsFilter ] } as any },
            { $expr: { $in: [ { $toString: "$_tsMetadata._telItemId" }, telItemIdsFilter ] } as any },
          ];
          console.log('  Applying telemetry item filter (supports type-agnostic _telItemId and _tsMetadata._telItemId):', telItemIdsFilter);
        }

        const pipeline = [
          { $match: match },
          { $sort: { _ts: 1 } }, // Sort ascending for sparkline
          { $limit: 1000 }, // More readings to cover multiple devices
        ];

        try {
          const response = await IafItemSvc.aggregateReadings(coll._id, pipeline, ctx);
          let readings = (response?._list || []) as Record<string, unknown>[];

          // Optionally post-process readings via script
          if (applyReadingProcessor && readingProcessorFn) {
            console.log('  Applying reading processor to', readings.length, 'readings', readingProcessorInfo ? `(script: ${readingProcessorInfo.name || readingProcessorInfo.scriptId})` : '');
            const processed: Record<string, unknown>[] = [];
            let changed = 0;
            for (const r of readings) {
              const pr = await readingProcessorFn(r, {PlatformApi, ...ctx});
              if (pr == null) continue; // allow processor to drop a reading
              processed.push(pr);
              if (pr !== r) changed++;
            }
            readings = processed;
            console.log(`  Processor result: kept ${readings.length}/${response?._list?.length || 0}, changed ${changed}`);
          }
            if (applyReadingProcessor) {
                console.log('  Applying test reading processor to', readings.length, 'readings', readings);
                const processed: Record<string, unknown>[] = [];
                let changed = 0;
                for (const r of readings) {
                    // Convert "ACTIVE(1)" or "INACTIVE(0)" format strings to numeric values
                    const pr = { ...r };
                    if (typeof pr.value === 'string') {
                        const match = pr.value.match(/\((\d+)\)/);
                        if (match) {
                            pr.value = parseInt(match[1], 10);
                            changed++;
                        }
                    }
                    if (pr == null) continue; // allow processor to drop a reading
                    processed.push(pr);
                }
                readings = processed;
                console.log(`  Processor result: kept ${readings.length}/${response?._list?.length || 0}, changed ${changed}`);
            }


            sensorReadingsMap[collectionName] = readings;
          console.log(`  Found ${readings.length} readings`);

          if (readings.length > 0) {
            console.log(`  Sample reading:`, readings[0]);
          }
        } catch (collErr) {
          console.error(`  Error fetching from ${collectionName}:`, collErr);
          sensorReadingsMap[collectionName] = [];
        }
      }

      // Build visualization data grouped by sourceId (device)
      const deviceVizData: DeviceViz[] = [];

      for (const [collName, readings] of Object.entries(sensorReadingsMap)) {
        if (readings.length === 0) continue;

        // Group readings by _sourceId (from _tsMetadata or direct field)
        const readingsByDevice: Record<string, Record<string, unknown>[]> = {};

        for (const reading of readings) {
          // Try to get sourceId from different possible locations
          let sourceId =
            (reading._tsMetadata as Record<string, unknown>)?._sourceId as string ||
            reading._sourceId as string ||
            'unknown';

          if (!readingsByDevice[sourceId]) {
            readingsByDevice[sourceId] = [];
          }
          readingsByDevice[sourceId].push(reading);
        }

        console.log(`  Devices found in ${collName}:`, Object.keys(readingsByDevice));

        // For each device, extract metrics
        for (const [sourceId, deviceReadings] of Object.entries(readingsByDevice)) {
          if (deviceReadings.length === 0) continue;

          // Get all numeric fields from first reading (excluding metadata)
          const sampleReading = deviceReadings[0];
          const numericFields = Object.keys(sampleReading).filter(key => {
            if (key.startsWith('_') || key === 'id') return false;
            return typeof sampleReading[key] === 'number';
          });

          const metrics: MetricViz[] = [];

          for (const fieldName of numericFields) {
            const values = deviceReadings
              .map(r => r[fieldName])
              .filter((v): v is number => typeof v === 'number');

            const timestamps = deviceReadings
              .map(r => r._ts)
              .filter((t): t is string => typeof t === 'string');

            if (values.length === 0) continue;

            // Find matching display point
            const matchingDisplayPoint = displayPoints.find(dp => dp.pointName === fieldName);

            const minVal = Math.min(...values);
            const maxVal = Math.max(...values);
            const avg = values.reduce((a, b) => a + b, 0) / values.length;
            const latest = values[values.length - 1];

            metrics.push({
              pointName: fieldName,
              displayPoint: matchingDisplayPoint || null,
              values,
              timestamps,
              min: minVal,
              max: maxVal,
              avg,
              latest,
            });
          }

          if (metrics.length > 0) {
            deviceVizData.push({
              sourceId,
              collectionName: collName,
              metrics,
            });
          }
        }
      }

      setDeviceReadingsViz(deviceVizData);

      console.log('\n' + '='.repeat(60));
      console.log('SENSOR READINGS SUMMARY');
      console.log('='.repeat(60));

      let totalReadings = 0;
      for (const [collName, readings] of Object.entries(sensorReadingsMap)) {
        const count = Array.isArray(readings) ? readings.length : 0;
        totalReadings += count;
        console.log(`${collName}: ${count} readings`);
      }

      const totalDevices = deviceVizData.length;
      const totalMetrics = deviceVizData.reduce((sum, d) => sum + d.metrics.length, 0);
      const metricsWithDisplayPoints = deviceVizData.reduce(
        (sum, d) => sum + d.metrics.filter(m => m.displayPoint).length, 0
      );

      console.log('='.repeat(60));
      console.log(`TOTAL: ${totalReadings} readings across ${collections.length} collections`);
      console.log(`Devices: ${totalDevices}, Metrics: ${totalMetrics}, With Display Points: ${metricsWithDisplayPoints}`);
      console.log('='.repeat(60));

      console.log('\nFull readings data:', sensorReadingsMap);
      console.log('\nDevice visualization data:', deviceVizData);

      setSuccessMessage(`Fetched ${totalReadings} readings from ${totalDevices} devices. Check console for details.`);
    } catch (err) {
      console.error('Error fetching sensor readings:', err);
      setError(`Failed to fetch sensor readings: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  // ============================================================================
  // DISPLAY POINTS MANAGEMENT
  // ============================================================================

  const fetchDisplayPoints = useCallback(async () => {
    if (!accessToken || !selectedProject) return;

    setIsLoading(true);
    setError(null);

    try {
      const ctx = getCtx();

      const collResult = await IafItemSvc.getNamedUserItems(
        { query: { _userType: DISPLAY_POINTS_USER_TYPE } },
        ctx,
        { _pageSize: 1 }
      );

      if (collResult._list?.length > 0) {
        const coll = collResult._list[0] as unknown as DisplayPointsCollection;
        setDisplayPointsCollection(coll);

        const itemsResult = await IafItemSvc.getRelatedItems(
          coll._userItemId,
          {},
          ctx,
          { page: { _pageSize: 1000 } }
        );
        setDisplayPoints((itemsResult._list || []) as unknown as DisplayPointItem[]);
      } else {
        setDisplayPointsCollection(null);
        setDisplayPoints([]);
      }
    } catch (err) {
      console.error('Error fetching display points:', err);
      setError(`Failed to fetch display points: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  }, [accessToken, selectedProject, getCtx]);

  const createDisplayPointsCollection = async () => {
    if (!accessToken || !selectedProject) {
      setError('No project selected');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const ctx = getCtx();

      const existingResult = await IafItemSvc.getNamedUserItems(
        { query: { _userType: DISPLAY_POINTS_USER_TYPE } },
        ctx,
        { _pageSize: 1 }
      );

      if (existingResult._list?.length > 0) {
        await IafItemSvc.deleteNamedUserItem(existingResult._list[0]._id, ctx, {});
      }

      const baseUri = IafFetch.CONFIG.itemServiceOrigin;
      const namespace = selectedProject._namespaces?.[0];

      const collectionData = {
        _itemClass: 'NamedUserCollection',
        _name: 'Telemetry Display Points Config',
        _shortName: 'telemetry_display_pts',
        _description: 'Telemetry Point Config',
        _userType: DISPLAY_POINTS_USER_TYPE,
        _namespaces: selectedProject._namespaces,
      };

      const createResponse = await fetch(
        `${baseUri}/itemsvc/api/v1/nameduseritems?nsfilter=${namespace}`,
        {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: accessToken,
          },
          body: JSON.stringify([collectionData]),
        }
      );

      if (!createResponse.ok) {
        throw new Error(`HTTP ${createResponse.status}`);
      }

      const createdCollections = await createResponse.json();
      console.log('Created collections response:', createdCollections);

      let collection = Array.isArray(createdCollections)
        ? createdCollections[0]
        : createdCollections._list?.[0] || createdCollections;

      if (!collection._userItemId) {
        console.log('_userItemId not in response, fetching collection...');
        const fetchResult = await IafItemSvc.getNamedUserItems(
          { query: { _userType: DISPLAY_POINTS_USER_TYPE } },
          ctx,
          { _pageSize: 1 }
        );
        if (fetchResult._list?.length > 0) {
          collection = fetchResult._list[0];
        } else {
          throw new Error('Collection created but could not be found');
        }
      }

      const rows = DEFAULT_DISPLAY_POINTS.map((config) => ({
        ...config,
        name: config.pointName,
        description: config.description || DISPLAY_POINT_DESCRIPTIONS[config.pointName] || '',
        dtTypes: config.dtTypes,
        types: config.dtTypes,
      }));

      await IafItemSvc.createRelatedItemsBulk(collection._userItemId, rows, ctx);

      setSuccessMessage('Display points collection created with default points!');
      await fetchDisplayPoints();
    } catch (err) {
      console.error('Error creating display points:', err);
      setError(`Failed to create display points: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const addDisplayPoint = async () => {
    if (!accessToken || !displayPointsCollection) {
      setError('No display points collection found. Create one first.');
      return;
    }

    if (!displayPointForm.pointName || !displayPointForm.displayName) {
      setError('Point Name and Display Name are required');
      return;
    }

    if (!displayPointForm.dtTypes || displayPointForm.dtTypes.length === 0) {
      setError('At least one Equipment Type (dtTypes) must be selected for the display point');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const collectionNamespaces = displayPointsCollection._namespaces || [];
      const projectNamespaces = selectedProject?._namespaces || [];
      const allNamespaces = [
        ...new Set([...collectionNamespaces])
      ];
      const ctx = getCtx();

      const newPoint = {
        ...displayPointForm,
        name: displayPointForm.pointName,
        types: displayPointForm.dtTypes,
        _id: displayPointForm._id
      };

      if(newPoint._id){
          await IafItemSvc.updateRelatedItems(
              displayPointsCollection._userItemId,
              [newPoint],
              ctx
          );

      } else {

          await IafItemSvc.createRelatedItemsBulk(
              displayPointsCollection._userItemId,
              [newPoint],
              ctx
          );
      }

      setSuccessMessage(`Display point "${displayPointForm.displayName}" created!`);
      setIsEditingDisplayPoint(false);
      setDisplayPointForm(getEmptyDisplayPointForm());
      setEditingDisplayPoint(null);
      await fetchDisplayPoints();
    } catch (err) {
      console.error('Error adding display point:', err);
      setError(`Failed to add display point: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const deleteDisplayPoint = async (point: DisplayPointItem) => {
    if (!accessToken || !displayPointsCollection || !confirm(`Delete display point "${point.displayName}"?`)) return;

    setIsLoading(true);
    setError(null);

    try {
      const collectionNamespaces = displayPointsCollection._namespaces || [];
      const projectNamespaces = selectedProject?._namespaces || [];
      const allNamespaces = [
        ...new Set([...projectNamespaces])
      ];
      const ctx = getCtx();

      await IafItemSvc.deleteRelatedItems(
        displayPointsCollection._userItemId,
        [point._id],
        ctx
      );

      setSuccessMessage(`Display point "${point.displayName}" deleted!`);
      await fetchDisplayPoints();
    } catch (err) {
      console.error('Error deleting display point:', err);
      setError(`Failed to delete display point: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const openDisplayPointEditor = (point?: DisplayPointItem) => {
    if (point) {
      setEditingDisplayPoint(point);
      setDisplayPointForm({
        _id: point._id,
        haystackTag: point.haystackTag,
        pointName: point.pointName,
        kind: point.kind,
        displayName: point.displayName,
        description: point.description,
        iconClass: point.iconClass,
        format: point.format,
        unit: point.unit,
        aggregation: point.aggregation,
        places: point.places,
        ranges: point.ranges,
        dtTypes: point.dtTypes,
      });
    } else {
      setEditingDisplayPoint(null);
      const empty = getEmptyDisplayPointForm();
      const code = selectedCollection ? getCollectionAssetTypeCode(selectedCollection) : '';
      setDisplayPointForm({ ...empty, dtTypes: code ? [code] : [] });
    }
    setIsEditingDisplayPoint(true);
  };

  const closeDisplayPointEditor = () => {
    setIsEditingDisplayPoint(false);
    setEditingDisplayPoint(null);
    setDisplayPointForm(getEmptyDisplayPointForm());
  };

  // Load display points when tab is selected
  useEffect(() => {
    if (activeTab === 'displayPoints' && accessToken && selectedProject) {
      fetchDisplayPoints();
    }
  }, [activeTab, fetchDisplayPoints, accessToken, selectedProject]);

  // ============================================================================
  // RENDER HELPERS
  // ============================================================================

  const getCollectionAssetType = (collection: TelemetryCollection): string => {
    const tipVersion = collection._versions?.find((v) => v._isTip);
    return tipVersion?._userAttributes?.['Asset Type'] || 'Unknown';
  };

  const getCollectionAggregation = (collection: TelemetryCollection): string => {
    const tipVersion = collection._versions?.find((v) => v._isTip);
    return tipVersion?._userAttributes?.aggregation || 'Unknown';
  };

  const filteredModelElements = selectedCollection
    ? modelElements.filter((el) => {
        const collAssetType = getCollectionAssetType(selectedCollection);
        return el.properties['Asset Type']?.val === collAssetType;
      })
    : modelElements;

  const getCollectionAssetTypeCode = (collection: TelemetryCollection): string => {
    const tipVersion = collection._versions?.find((v) => v._isTip);
    return (tipVersion?._userAttributes as any)?.assetTypeCode || '';
  };

  // Helper: derived options
  const catalogCategories = Array.from(new Set(catalogEntries.map(e => e.category))).sort();
  const catalogTypesForCategory = (cat: string) => catalogEntries.filter(e => e.category === cat);

  // Keep pickerTypeCode in sync when not overridden
  useEffect(() => {
    if (!pickerOverrideCode) {
      setPickerTypeCode(generateTypeCode(pickerTypeName));
    }
  }, [pickerTypeName, pickerOverrideCode]);

  // Keep catalog form code in sync if not overridden
  useEffect(() => {
    if (!catFormOverrideCode) {
      setCatFormTypeCode(generateTypeCode(catFormTypeName));
    }
  }, [catFormTypeName, catFormOverrideCode]);

  // Catalog actions
  const handleCreateOrSeedCatalog = useCallback(async () => {
    if (!selectedProject || !accessToken) return;
    setIsCatalogLoading(true);
    setCatalogError(null);
    try {
      const ctx = getCtx();
      let coll = await getCatalogCollection(ctx);
      if (!coll) {
        // Use ONLY project's primary namespace (not multiple namespaces)
        const ns0 = selectedProject._namespaces?.[0];
        coll = await createCatalogCollection(ctx, ns0 ? [ns0] : [], 'Equipment Types Catalog');
      }
      if (coll) {
        setCatalogCollection(coll);
        await seedCatalogFromJson(coll, ctx);
        const entries = await listCatalogEntries(coll, ctx);
        setCatalogEntries(entries);
      }
    } catch (e: any) {
      console.error('Create/Seed catalog failed', e);
      setCatalogError(e?.message || 'Failed to create/seed catalog');
    } finally {
      setIsCatalogLoading(false);
    }
  }, [selectedProject, accessToken, getCtx]);

  const handleSeedFromJson = useCallback(async () => {
    if (!catalogCollection) return;
    setIsCatalogLoading(true);
    setCatalogError(null);
    try {
      const ctx = getCtx();
      await seedCatalogFromJson(catalogCollection, ctx);
      const entries = await listCatalogEntries(catalogCollection, ctx);
      setCatalogEntries(entries);
    } catch (e: any) {
      console.error('Seed from JSON failed', e);
      setCatalogError(e?.message || 'Failed to seed from JSON');
    } finally {
      setIsCatalogLoading(false);
    }
  }, [catalogCollection, getCtx]);

  const handleSubmitCatalogEntry = useCallback(async () => {
    if (!catalogCollection) {
      setCatalogError('Catalog collection is not created');
      return;
    }
    if (!catFormCategory || !catFormTypeName || !catFormTypeCode) {
      setCatalogError('Category, Type Name, and Type Code are required');
      return;
    }
    // Uniqueness check on (category, typeCode), case-insensitive
    const key = `${catFormCategory.toLowerCase()}|${catFormTypeCode.toLowerCase()}`;
    const exists = catalogEntries.some(e => `${e.category.toLowerCase()}|${e.typeCode.toLowerCase()}` === key && e._id !== editingCatalogEntryId);
    if (exists) {
      setCatalogError('An entry with the same Category and Type Code already exists');
      return;
    }
    setIsCatalogLoading(true);
    setCatalogError(null);
    try {
      const ctx = getCtx();
      const entry: CatalogEntry = { category: catFormCategory, typeName: catFormTypeName, typeCode: catFormTypeCode };
      if (editingCatalogEntryId) {
        await updateCatalogEntry(catalogCollection, editingCatalogEntryId, entry, ctx);
      } else {
        await addCatalogEntry(catalogCollection, entry, ctx);
      }
      const entries = await listCatalogEntries(catalogCollection, ctx);
      setCatalogEntries(entries);
      // reset form
      setEditingCatalogEntryId(null);
      setCatFormCategory('');
      setCatFormTypeName('');
      setCatFormTypeCode('');
      setCatFormOverrideCode(false);
    } catch (e: any) {
      console.error('Submit catalog entry failed', e);
      setCatalogError(e?.message || 'Failed to save catalog entry');
    } finally {
      setIsCatalogLoading(false);
    }
  }, [catalogCollection, catFormCategory, catFormTypeName, catFormTypeCode, editingCatalogEntryId, catalogEntries, getCtx]);

  const startEditCatalogEntry = (entry: CatalogEntry) => {
    setEditingCatalogEntryId(entry._id || null);
    setCatFormCategory(entry.category);
    setCatFormTypeName(entry.typeName);
    setCatFormTypeCode(entry.typeCode);
    setCatFormOverrideCode(true);
  };

  const handleDeleteCatalogEntry = useCallback(async (entry: CatalogEntry) => {
    if (!catalogCollection) return;
    if (!confirm(`Delete equipment type ${entry.typeName} (${entry.typeCode}) in ${entry.category}?`)) return;
    setIsCatalogLoading(true);
    setCatalogError(null);
    try {
      const ctx = getCtx();
      if (entry._id) {
        await deleteCatalogEntry(catalogCollection, entry._id, ctx);
      }
      const entries = await listCatalogEntries(catalogCollection, ctx);
      setCatalogEntries(entries);
    } catch (e: any) {
      console.error('Delete catalog entry failed', e);
      setCatalogError(e?.message || 'Failed to delete catalog entry');
    } finally {
      setIsCatalogLoading(false);
    }
  }, [catalogCollection, getCtx]);

  // ============================================================================
  // RENDER
  // ============================================================================

  if (!selectedProject) {
    return (
      <div className="telemetry-management">
        <div className="telemetry-empty-state">
          <i className="fas fa-exclamation-triangle"></i>
          <h2>No Project Selected</h2>
          <p>Please select a project to manage telemetry.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="telemetry-management">
      <div className="telemetry-header">
        <div className="telemetry-title">
          <i className="fas fa-chart-line"></i>
          <h1>Telemetry Management</h1>
        </div>
        <p className="telemetry-subtitle">
          Manage telemetry collections, items, and display points for{' '}
          <strong>{selectedProject._name}</strong>
        </p>
        <div className="telemetry-actions">
          <a href="#/admin/equipment-catalog" className="btn-link" title="Open Equipment Catalog">
            <i className="fas fa-tags"></i>
            Manage Equipment Catalog
          </a>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="telemetry-message error">
          <i className="fas fa-exclamation-circle"></i>
          <span>{error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}
      {successMessage && (
        <div className="telemetry-message success">
          <i className="fas fa-check-circle"></i>
          <span>{successMessage}</span>
          <button onClick={() => setSuccessMessage(null)}>×</button>
        </div>
      )}

      {/* Tabs */}
      <div className="telemetry-tabs">
        <button
          className={`tab-button ${activeTab === 'collections' ? 'active' : ''}`}
          onClick={() => setActiveTab('collections')}
        >
          <i className="fas fa-database"></i>
          Collections
        </button>
        <button
          className={`tab-button ${activeTab === 'items' ? 'active' : ''}`}
          onClick={() => setActiveTab('items')}
        >
          <i className="fas fa-microchip"></i>
          Telemetry Items
        </button>
        <button
          className={`tab-button ${activeTab === 'displayPoints' ? 'active' : ''}`}
          onClick={() => setActiveTab('displayPoints')}
        >
          <i className="fas fa-sliders-h"></i>
          Display Points
        </button>
      </div>

      {/* Tab Content */}
      <div className="telemetry-content">
        {/* Collections Tab */}
        {activeTab === 'collections' && (
          <div className="tab-content">

            <div className="section-card">
              <div className="section-header">
                <h2>
                  <i className="fas fa-list"></i>
                  Collections
                </h2>
                <div className="actions">
                  <button className="btn-secondary" onClick={fetchCollections} disabled={isLoading}>
                    <i className="fas fa-sync-alt"></i>
                    Refresh
                  </button>
                  <button className="btn-primary" onClick={openCreateCollectionModal} disabled={isLoading || catalogCategories.length === 0}>
                    <i className="fas fa-plus"></i>
                    Create Collection
                  </button>
                </div>
              </div>

              {/* Collections Filters & Paging */}
              <div className="filters-row" style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', margin: '0 16px 12px' }}>
                <div className="form-group" style={{ minWidth: 260 }}>
                  <label style={{ fontSize: 12 }}>Filter by _userType</label>
                  <select
                    className="form-control"
                    value={selectedUserType}
                    onChange={(e) => setSelectedUserType(e.target.value)}
                  >
                    <option value="">All types</option>
                    {availableUserTypes.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
                {/* Client-side paging is always used; we fetch all pages via loop */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <label style={{ fontSize: 12 }}>Page size</label>
                  <select
                    className="form-control"
                    style={{ width: 90 }}
                    value={collPageSize}
                    onChange={(e) => setCollPageSize(parseInt(e.target.value, 10) || 50)}
                  >
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>
                <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 12, color: '#555' }}>
                    {(() => {
                      const start = collTotal === 0 ? 0 : collPage * collPageSize + 1;
                      const end = Math.min((collPage + 1) * collPageSize, collTotal);
                      const totalPages = collTotal > 0 ? Math.ceil(collTotal / collPageSize) : 1;
                      return `Showing ${start}-${end} of ${collTotal} • Page ${Math.min(collPage + 1, totalPages)} of ${totalPages}`;
                    })()}
                  </span>
                  <button
                    className="btn-secondary"
                    onClick={() => setCollPage((p) => Math.max(0, p - 1))}
                    disabled={isLoading || collPage === 0}
                    title="Previous page"
                  >
                    <i className="fas fa-chevron-left"></i>
                  </button>
                  <button
                    className="btn-secondary"
                    onClick={() => {
                      const totalPages = collTotal > 0 ? Math.ceil(collTotal / collPageSize) : 1;
                      setCollPage((p) => Math.min(totalPages - 1, p + 1));
                    }}
                    disabled={isLoading || ((collPage + 1) * collPageSize >= collTotal)}
                    title="Next page"
                  >
                    <i className="fas fa-chevron-right"></i>
                  </button>
                </div>
              </div>

              {isLoading && collections.length === 0 ? (
                <div className="loading-state">
                  <i className="fas fa-spinner fa-spin"></i>
                  <span>Loading collections...</span>
                </div>
              ) : collections.length === 0 ? (
                <div className="empty-state">
                  <i className="fas fa-inbox"></i>
                  <p>No telemetry collections found</p>
                </div>
              ) : (
                <div className="collections-grid">
                  {collections.map((collection) => {
                    const assetTypeCode = getCollectionAssetTypeCode(collection);
                    const assetType = getCollectionAssetType(collection);
                    const hasMatchingEquipmentType = catalogEntries.some(
                      entry => entry.typeCode === assetTypeCode || entry.typeName === assetType
                    );
                    return (
                    <div
                      key={collection._id}
                      className={`collection-card ${selectedCollection?._id === collection._id ? 'selected' : ''}`}
                      onClick={() => setSelectedCollection(collection)}
                    >
                      <div className="collection-header">
                        <h3>{collection._name}</h3>
                        <button
                          className="btn-icon danger"
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteCollection(collection);
                          }}
                          title="Delete collection"
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </div>
                      <div className="collection-meta">
                        <span className="meta-item">
                          <i className="fas fa-tag"></i>
                          {assetType}
                        </span>
                        <span className="meta-item">
                          <i className="fas fa-layer-group"></i>
                          {getCollectionAggregation(collection)}
                        </span>
                      </div>
                      {!hasMatchingEquipmentType && (
                        <div className="collection-warning">
                          <i className="fas fa-exclamation-triangle"></i>
                          <span>No matching equipment type found in catalog</span>
                        </div>
                      )}
                      <div className="collection-details">
                        <code>{collection._shortName}</code>
                        <p>{collection._description}</p>
                      </div>
                    </div>
                  );})}

                </div>
              )}
            </div>


              {/* Sensor Readings Visualization - Grouped by Device */}
              <div className="section-card">
                  <h2>
                      <i className="fas fa-chart-area"></i>
                      Sensor Readings (Last 24h · max 1000 rows shared by {selectedTelItemIds.length} selected items)
                  </h2>
                  {!selectedCollection && (
                      <p className="section-subtitle">
                          Please select a telemetry collection to view sensor readings.
                      </p>
                  )}
                  {selectedCollection && (
                      <div className="reading-toolbar">
                        <div className="left">
                          <button
                            className="btn-secondary"
                            onClick={() => setShowReadingFilters(v => !v)}
                            disabled={isLoading}
                            title="Show/Hide filter controls"
                          >
                            <i className="fas fa-sliders-h" style={{ marginRight: 6 }}></i>
                            {showReadingFilters ? 'Hide filters' : 'Show filters'}
                          </button>
                        </div>
                        <div className="right">
                          <span className="mini-muted">
                            Selected {selectedTelItemIds.length} / {telemetryItems.length} items
                          </span>
                        </div>
                      </div>
                  )}
                  {selectedCollection && (
                    <div className={`filters-panel ${showReadingFilters ? 'open' : ''}`}>
                      <div className="filters-grid">
                        <div className="filter-col">
                          <label className="filter-label">Filter by Telemetry Items</label>
                          {telemetryItems.length === 0 ? (
                            <div className="mini-muted">No telemetry items in this collection</div>
                          ) : (
                            <select
                              className="telemetry-multiselect"
                              multiple
                              size={Math.min(10, Math.max(6, Math.min(telemetryItems.length, 12)))}
                              value={selectedTelItemIds}
                              onChange={(e) =>
                                setSelectedTelItemIds(Array.from(e.target.selectedOptions).map(o => o.value))
                              }
                              disabled={isLoading}
                            >
                              {telemetryItems.map((item) => (
                                <option key={item._id} value={item._id} title={item._sourceId}>
                                  {item.name || item._sourceId}
                                </option>
                              ))}
                            </select>
                          )}
                          <div className="filter-actions">
                            {telemetryItems.length > 0 && (
                              <>
                                <button
                                  className="btn-secondary"
                                  onClick={() => setSelectedTelItemIds(telemetryItems.map(t => t._id))}
                                  disabled={isLoading}
                                >
                                  Select all
                                </button>
                                <button
                                  className="btn-secondary"
                                  onClick={() => setSelectedTelItemIds([])}
                                  disabled={isLoading}
                                >
                                  Clear
                                </button>
                              </>
                            )}
                          </div>
                        </div>

                        {/* Reading processor controls */}
                        <div className="filter-col">
                          <label className="filter-label">Reading Processing Script</label>
                          <div className="processor-row">
                            <input
                              id="applyReadingProcessorToggle"
                              type="checkbox"
                              checked={applyReadingProcessor}
                              onChange={(e) => setApplyReadingProcessor(e.target.checked)}
                              disabled={isLoading || !readingProcessorFn}
                            />
                            <label htmlFor="applyReadingProcessorToggle" className="processor-label">
                              Apply if available
                            </label>
                            <button
                              className="btn-secondary"
                              onClick={() => void loadReadingProcessor()}
                              disabled={isLoading}
                              title="Reload script"
                            >
                              Reload
                            </button>
                          </div>
                          <div className={`mini-muted ${readingProcessorFn ? 'ok' : 'warn'}`}>
                            {readingProcessorFn
                              ? (
                                <span>
                                  Loaded script: <strong>{readingProcessorInfo?.name || readingProcessorInfo?.scriptId}</strong>
                                </span>
                              )
                              : (
                                <span>No script found with user type "reading_process"</span>
                              )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {selectedCollection && (
                      <div className="reading-toolbar">
                          <div className="left">
                              <button className="btn-secondary" onClick={fetchSensorReadings} disabled={isLoading}>
                                  <i className="fas fa-download"></i>
                                  Fetch Readings
                              </button>
                          </div>
                          <div className="right">
                          </div>
                      </div>
                  )}
                  {(deviceReadingsViz.length > 0 && selectedCollection) && (
                      <div>
                          <p className="section-subtitle">
                              {deviceReadingsViz.length} devices found, {deviceReadingsViz.reduce((sum, d) => sum + d.metrics.length, 0)} total metrics
                          </p>

                          {deviceReadingsViz.map((device, deviceIdx) => (
                              <div key={`device-${device.sourceId}-${deviceIdx}`} className="device-viz-card">
                                  <div className="device-viz-header">
                                      <div className="device-info">
                                          <i className="fas fa-microchip"></i>
                                          <span className="device-id" title={device.sourceId}>
                          {device.sourceId.length > 30
                              ? device.sourceId.substring(0, 30) + '...'
                              : device.sourceId}
                        </span>
                                      </div>
                                      <div className="device-meta">
                                          <span className="collection-tag">{device.collectionName}</span>
                                          <span className="metric-count">{device.metrics.length} metrics</span>
                                      </div>
                                  </div>

                                  <div className="sparklines-grid">
                                      {device.metrics.map((metric, metricIdx) => (
                                          <div
                                              key={`${device.sourceId}-${metric.pointName}-${metricIdx}`}
                                              className={`sparkline-card ${metric.displayPoint ? 'has-display-point' : ''}`}
                                          >
                                              <div className="sparkline-header">
                            <span className="sparkline-name" title={metric.pointName}>
                              {metric.displayPoint?.displayName || metric.pointName}
                            </span>
                                                  {metric.displayPoint && (
                                                      <span className="sparkline-badge" title="Display point configured">
                                <i className="fas fa-check-circle"></i>
                              </span>
                                                  )}
                                              </div>
                                              <div className="sparkline-chart">
                                                  <SparklineSVG
                                                      values={metric.values}
                                                      displayPoint={metric.displayPoint}
                                                      width={160}
                                                      height={40}
                                                  />
                                              </div>
                                              <div className="sparkline-stats">
                                                  <div className="stat">
                                                      <span className="stat-label">Latest</span>
                                                      <span className="stat-value">{metric.latest.toFixed(2)}</span>
                                                  </div>
                                                  <div className="stat">
                                                      <span className="stat-label">Avg</span>
                                                      <span className="stat-value">{metric.avg.toFixed(2)}</span>
                                                  </div>
                                                  <div className="stat">
                                                      <span className="stat-label">Range</span>
                                                      <span className="stat-value">{metric.min.toFixed(1)} - {metric.max.toFixed(1)}</span>
                                                  </div>
                                              </div>
                                              <div className="sparkline-meta">
                                                  <span className="reading-count">{metric.values.length} readings</span>
                                                  {metric.displayPoint?.unit && (
                                                      <span className="unit-tag">{metric.displayPoint.unit}</span>
                                                  )}
                                              </div>
                                              {metric.timestamps.length > 0 && (
                                                  <div className="sparkline-time-range">
                                                      <i className="fas fa-clock"></i>
                                                      <span>
                                {new Date(metric.timestamps[0]).toLocaleString([], {
                                    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                                })}
                                                          {' → '}
                                                          {new Date(metric.timestamps[metric.timestamps.length - 1]).toLocaleString([], {
                                                              month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                                                          })}
                              </span>
                                                  </div>
                                              )}
                                          </div>
                                      ))}
                                  </div>
                              </div>
                          ))}
                      </div>
                  )}
              </div>
          </div>
        )}


        {/* Telemetry Items Tab */}
        {activeTab === 'items' && (
          <div className="tab-content">
            <div className="section-card">
              <h2>
                <i className="fas fa-database"></i>
                Select Collection
              </h2>
              <select
                className="collection-select"
                value={selectedCollection?._id || ''}
                onChange={(e) => {
                  const coll = collections.find((c) => c._id === e.target.value);
                  setSelectedCollection(coll || null);
                }}
                disabled={isLoading}
              >
                <option value="">-- Select a collection --</option>
                {collections.map((collection) => (
                  <option key={collection._id} value={collection._id}>
                    {collection._name} ({getCollectionAggregation(collection)})
                  </option>
                ))}
              </select>
            </div>

            {selectedCollection && (
              <>
                <div className="section-card">
                  <h2>
                    <i className="fas fa-plus-circle"></i>
                    Add Telemetry Item
                  </h2>
                  <div className="form-info">
                    <i className="fas fa-info-circle"></i>
                    <span>
                      {useApiForElements
                        ? 'Model elements fetched from API'
                        : 'Using hardcoded MODEL_ELEMENTS (toggle to use API in future)'}
                    </span>
                    <label className="toggle-label">
                      <input
                        type="checkbox"
                        checked={useApiForElements}
                        onChange={(e) => setUseApiForElements(e.target.checked)}
                        disabled
                      />
                      Use API (Coming Soon)
                    </label>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Model Element (_sourceId)</label>
                      <select
                        value={selectedModelElement}
                        onChange={(e) => {
                          setSelectedModelElement(e.target.value);
                          const el = modelElements.find((m) => m.source_id === e.target.value);
                          if (el) {
                            setNewItemName(el.properties['BA Name']?.val || '');
                          }
                        }}
                        disabled={isLoading}
                      >
                        <option value="">-- Select model element --</option>
                        {filteredModelElements.map((element) => (
                          <option key={element.source_id} value={element.source_id}>
                            {element.properties['BA Name']?.val || element.source_id} (
                            {element.properties['Asset Type']?.val})
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Device Name</label>
                      <input
                        type="text"
                        value={newItemName}
                        onChange={(e) => setNewItemName(e.target.value)}
                        placeholder="Enter device name..."
                        disabled={isLoading}
                      />
                    </div>
                    <button
                      className="btn-primary"
                      onClick={createTelemetryItem}
                      disabled={isLoading || !selectedModelElement}
                    >
                      {isLoading ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-plus"></i>}
                      Add Item
                    </button>
                  </div>
                </div>

                <div className="section-card">
                  <div className="section-header">
                    <h2>
                      <i className="fas fa-list"></i>
                      Items in Collection
                    </h2>
                    <span className="item-count">{telemetryItems.length} items</span>
                  </div>

                  {isLoading && telemetryItems.length === 0 ? (
                    <div className="loading-state">
                      <i className="fas fa-spinner fa-spin"></i>
                      <span>Loading items...</span>
                    </div>
                  ) : telemetryItems.length === 0 ? (
                    <div className="empty-state">
                      <i className="fas fa-inbox"></i>
                      <p>No telemetry items in this collection</p>
                    </div>
                  ) : (
                    <div className="items-table">
                      <table>
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Source ID</th>
                            <th>Device Type</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {telemetryItems.map((item) => (
                            <tr key={item._id}>
                              <td>{item.name}</td>
                              <td>
                                <code>{item._sourceId}</code>
                              </td>
                              <td>{item.deviceType}</td>
                              <td>
                                <button
                                  className="btn-icon danger"
                                  onClick={() => deleteTelemetryItem(item)}
                                  title="Delete item"
                                >
                                  <i className="fas fa-trash"></i>
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {/* Display Points Tab */}
        {activeTab === 'displayPoints' && (
          <div className="tab-content">
            <div className="section-card">
              <div className="section-header">
                <h2>
                  <i className="fas fa-sliders-h"></i>
                  Display Points Collection
                </h2>
                <div className="header-actions">
                  <button className="btn-secondary" onClick={fetchDisplayPoints} disabled={isLoading}>
                    <i className="fas fa-sync-alt"></i>
                    Refresh
                  </button>
                  <button
                    className="btn-primary"
                    onClick={() => openDisplayPointEditor()}
                    disabled={isLoading || !displayPointsCollection}
                  >
                    <i className="fas fa-plus"></i>
                    Add Point
                  </button>
                </div>
              </div>

              {displayPointsCollection ? (
                <div className="collection-status success">
                  <i className="fas fa-check-circle"></i>
                  <span>Collection exists: <strong>{displayPointsCollection._name}</strong></span>
                  <span className="point-count">{displayPoints.length} points</span>
                </div>
              ) : (
                <div className="collection-status warning">
                  <i className="fas fa-exclamation-triangle"></i>
                  <span>No display points collection found</span>
                  <button
                    className="btn-primary"
                    onClick={createDisplayPointsCollection}
                    disabled={isLoading}
                  >
                    {isLoading ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-plus"></i>}
                    Create with Defaults
                  </button>
                </div>
              )}
            </div>

            {displayPointsCollection && (
              <div className="section-card">
                <div className="section-header">
                  <h2>
                    <i className="fas fa-list"></i>
                    Display Points ({displayPoints.length})
                  </h2>
                  <button
                    className="btn-secondary danger-outline"
                    onClick={createDisplayPointsCollection}
                    disabled={isLoading}
                    title="Delete all and recreate with defaults"
                  >
                    <i className="fas fa-redo"></i>
                    Reset to Defaults
                  </button>
                </div>

                {isLoading && displayPoints.length === 0 ? (
                  <div className="loading-state">
                    <i className="fas fa-spinner fa-spin"></i>
                    <span>Loading display points...</span>
                  </div>
                ) : displayPoints.length === 0 ? (
                  <div className="empty-state">
                    <i className="fas fa-inbox"></i>
                    <p>No display points found</p>
                    <button className="btn-primary" onClick={() => openDisplayPointEditor()}>
                      <i className="fas fa-plus"></i>
                      Add First Point
                    </button>
                  </div>
                ) : (
                  <div className="display-points-grid">
                    {displayPoints.map((point) => {
                      const selectedCode = selectedCollection ? getCollectionAssetTypeCode(selectedCollection) : '';
                      const selectedType = selectedCollection ? getCollectionAssetType(selectedCollection) : '';
                      // Check if display point types match the selected collection's equipment type
                      // Display point dtTypes can contain either type codes (e.g., "AHU") or type names (e.g., "Air Handling Unit (AHU)")
                      const mismatched = !!selectedCode && !point.dtTypes.some(dt => {
                        // Direct match with code or type name
                        if (dt === selectedCode || dt === selectedType) return true;
                        // Check if any catalog entry matches both the display point type and the selected collection type
                        return catalogEntries.some(entry =>
                          (entry.typeCode === dt || entry.typeName === dt) &&
                          (entry.typeCode === selectedCode || entry.typeName === selectedType)
                        );
                      });
                      return (
                      <div key={point._id} className="display-point-card">
                        <div className="point-header">
                          <i className={point.iconClass}></i>
                          <h3>{point.displayName}</h3>
                          <div className="point-actions">
                            <button
                              className="btn-icon"
                              onClick={() => openDisplayPointEditor(point)}
                              title="Edit point"
                            >
                              <i className="fas fa-edit"></i>
                            </button>
                            <button
                              className="btn-icon danger"
                              onClick={() => deleteDisplayPoint(point)}
                              title="Delete point"
                            >
                              <i className="fas fa-trash"></i>
                            </button>
                          </div>
                        </div>
                        <div className="point-meta">
                          <span className="point-name">{point.pointName}</span>
                          <span className="point-unit">{point.unit}</span>
                          <span className="point-agg">{point.aggregation}</span>
                        </div>
                        <p className="point-description">{point.description}</p>
                        <div className="point-ranges">
                          {point.ranges.map((range, idx) => (
                            <div
                              key={idx}
                              className="range-badge"
                              style={{ backgroundColor: range.color }}
                              title={`${range.from} to ${range.to}`}
                            >
                              {range.name}
                            </div>
                          ))}
                        </div>
                        <div className="point-types">
                          {point.dtTypes.map((type) => (
                            <span key={type} className="type-badge">
                              {type}
                            </span>
                          ))}
                        </div>
                        {mismatched && (
                          <div className="point-mismatch-warning" title={`This display point is not configured for ${selectedType} (${selectedCode})`}>
                            <i className="fas fa-exclamation-circle"></i>
                            <span>Not available for this equipment type</span>
                          </div>
                        )}
                      </div>
                    );})}
                  </div>
                )}
              </div>
            )}

            <div className="section-card">
              <h2>
                <i className="fas fa-book"></i>
                Default Templates ({DEFAULT_DISPLAY_POINTS.length})
              </h2>
              <p className="section-description">
                These are the default display point configurations. Click "Reset to Defaults" above to restore them.
              </p>
              <details className="templates-accordion">
                <summary>View Default Templates</summary>
                <div className="display-points-grid small">
                  {DEFAULT_DISPLAY_POINTS.map((point) => (
                    <div key={point.pointName} className="display-point-card compact">
                      <div className="point-header">
                        <i className={point.iconClass}></i>
                        <h3>{point.displayName}</h3>
                      </div>
                      <div className="point-meta">
                        <span className="point-name">{point.pointName}</span>
                        <span className="point-unit">{point.unit}</span>
                      </div>
                      <div className="point-types">
                        {point.dtTypes.map((type) => (
                          <span key={type} className="type-badge">
                            {type}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </details>
            </div>
          </div>
        )}
      </div>

      {/* Display Point Editor Modal */}
      <DisplayPointEditor
        isOpen={isEditingDisplayPoint}
        editingPoint={editingDisplayPoint}
        form={displayPointForm}
        isLoading={isLoading}
        catalogEntries={catalogEntries}
        onFormChange={setDisplayPointForm}
        onSave={addDisplayPoint}
        onClose={closeDisplayPointEditor}
      />

      {/* Create Collection Modal */}
      {isCreateCollectionModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>
                <i className="fas fa-plus-circle"></i>
                Create New Collection
              </h2>
              <button
                className="btn-icon"
                onClick={() => setIsCreateCollectionModalOpen(false)}
                title="Close"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group">
                  <label>Equipment Category</label>
                  <select
                    value={pickerCategory}
                    onChange={(e) => {
                      setPickerCategory(e.target.value);
                      const types = catalogTypesForCategory(e.target.value);
                      const first = types[0];
                      setPickerTypeName(first?.typeName || '');
                      setPickerTypeCode(first?.typeCode || '');
                    }}
                    disabled={isLoading || isCatalogLoading}
                  >
                    <option value="">Select a category</option>
                    {catalogCategories.map((cat) => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Equipment Type</label>
                  <select
                    value={pickerTypeName}
                    onChange={(e) => {
                      const name = e.target.value;
                      setPickerTypeName(name);
                      const matchingEntry = catalogTypesForCategory(pickerCategory).find(t => t.typeName === name);
                      setPickerTypeCode(matchingEntry?.typeCode || '');
                    }}
                    disabled={isLoading || !pickerCategory}
                  >
                    <option value="">Select a type</option>
                    {catalogTypesForCategory(pickerCategory).map((t) => (
                      <option key={t.typeCode} value={t.typeName}>{t.typeName}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group full-width">
                  <label>Aggregation Prefix</label>
                  <select
                    value={newCollectionAggregation}
                    onChange={(e) => handleAggregationChange(e.target.value as AggregationType)}
                    disabled={isLoading}
                  >
                    <option value="raw">raw</option>
                    <option value="minutes">minutes</option>
                    <option value="hourly">hourly</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Granularity</label>
                  <select
                    value={newCollectionGranularity}
                    onChange={(e) => setNewCollectionGranularity(e.target.value as 'seconds' | 'minutes' | 'hours')}
                    disabled={isLoading}
                  >
                    <option value="seconds">seconds</option>
                    <option value="minutes">minutes</option>
                    <option value="hours">hours</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Expiry (seconds)</label>
                  <input
                    type="number"
                    value={newCollectionExpiry}
                    onChange={(e) => setNewCollectionExpiry(parseInt(e.target.value) || 0)}
                    disabled={isLoading}
                    placeholder="e.g., 86400 for 24 hours"
                  />
                  <span style={{fontSize: '0.75rem', color: 'rgba(255, 255, 255, 0.5)', marginTop: '0.25rem'}}>
                    {Math.floor(newCollectionExpiry / 86400)} days, {Math.floor((newCollectionExpiry % 86400) / 3600)} hours
                  </span>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn-secondary"
                onClick={() => setIsCreateCollectionModalOpen(false)}
              >
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={createCollection}
                disabled={isLoading || !pickerCategory || !pickerTypeName || !pickerTypeCode}
              >
                {isLoading ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-plus"></i>}
                Create Collection
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TelemetryManagementPage;
